import FormBuilderOb from '@components/FormBuilder/FormBuilder.ob';
import { FormIds } from '@utils/enums/FormIds.enum';
import { InsuranceInfo, Patient, usePatientStore } from '@store/patientStore';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import { ErrorMessage, GenericObj, Loader } from '@gemini/lux-ds-pms';
import { deepEqual, getInsuranceName } from '@utils/functions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import { ISaveEvent } from '@utils/events/events';
import { useHistory } from 'react-router-dom';

interface InsuranceProps {
  setEnableButton: (val: boolean) => void;
}

const InsurancePage = ({ setEnableButton }: InsuranceProps) => {
  const updatePatient = usePatientStore(state => state.updatePatient);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const history = useHistory();
  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const currPatientIndex = useOnlineBookingStore(state => state.currentPatient);
  const setChooseNewSlot = usePatientStore(
    state => state.setChooseNewSlotScheduleExam,
  );
  const patients = usePatientStore(state => state.patients);
  const payloadSaveEvent = useRef<ISaveEvent | undefined>(undefined);
  const [isSaved, setIsSaved] = useState<boolean[]>(patients.map(() => false));

  const {
    data: insurances,
    isLoading,
    isError,
  } = useGetInsuranceConfiguration({
    organizationId,
  });

  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >([]);

  const enableButtonMaster = useMemo(() => {
    return listEnableButton.every(
      enable => (typeof enable === 'boolean' && enable) || enable === undefined,
    );
  }, [listEnableButton]);

  const handleEnableButton = useCallback((index: number, enable: boolean) => {
    setListEnableButton(prev => {
      if (typeof prev[index] !== 'undefined' && prev[index] !== enable) {
        const newVal = [...prev];
        newVal[index] = enable;
        return newVal;
      }
      return prev;
    });
  }, []);

  const handleSave = useCallback(
    (
      index: number,
      payload: ISaveEvent,
      patient?: Patient,
      formData?: GenericObj,
    ) => {
      payloadSaveEvent.current = payload;
      console.log('#InsurancePage | handleSave', index, patient, formData);
      if (formData) {
        console.log(
          '#formData: ',
          formData,
          index,
          enableButtonMaster,
          !patients[index],
        );
        const newFormData = { ...formData } as unknown as {
          insurance: InsuranceInfo;
        };
        newFormData.insurance.isModified =
          patient && patient.scheduleExam && patient.insurance
            ? patient.insurance.id !== newFormData.insurance.id
            : false;
        newFormData.insurance.name = getInsuranceName(
          insurances,
          newFormData?.insurance?.id,
        );
        const isEqual = deepEqual(
          newFormData.insurance,
          patient?.insurance || {},
        );
        console.log('#formData.isEqual: ', isEqual, newFormData);
        if (!isEqual) {
          updatePatient(index, FormIds.Insurance, newFormData, insurances);
          if (
            patient?.extraInfoScheduleExam?.chooseNewSlot &&
            newFormData?.insurance?.id === ''
          ) {
            setChooseNewSlot(index, undefined);
          }
        }
        setIsSaved(prev => {
          const newVal = [...prev];
          newVal[index] = true;
          return newVal;
        });
      }
    },
    [enableButtonMaster, insurances, patients, setChooseNewSlot, updatePatient],
  );

  useEffect(() => {
    setEnableButton(enableButtonMaster);
  }, [enableButtonMaster]);

  useEffect(() => {
    setListEnableButton(patients.map(p => (p ? false : undefined)));
  }, [patients]);

  useEffect(() => {
    if (isSaved.every(s => s) && payloadSaveEvent.current) {
      history.push(`${payloadSaveEvent.current.propPage.next.url}`);
      setFormStep(payloadSaveEvent.current.propPage.next.step);
    }
  }, [isSaved, setEnableButton]);

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_insurance_title')}</title>
      </Helmet>
      {isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorMessage>
          {/* TODO gestire msg di errore{error?.message} */}
        </ErrorMessage>
      ) : (
        insurances &&
        patients && (
          <>
            {patients.map((patient, index) => {
              return (
                <FormBuilderOb
                  key={index}
                  formIds={[FormIds.Insurance]}
                  isVisible={currPatientIndex === index}
                  setEnableButton={enable => handleEnableButton(index, enable)}
                  handleSave={(payload, formData) =>
                    handleSave(index, payload, patient, formData)
                  }
                  optionsList={[
                    {
                      insurances: insurances,
                    },
                  ]}
                  patientIndex={index}
                  mykey={index.toString()}
                />
              );
            })}
            <MultiplePatientsNavigator
              style={{ padding: '0rem 0rem 1.5rem 0rem' }}
            />
          </>
        )
      )}
    </>
  );
};

export default InsurancePage;
